import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';
import { FeatureContext } from '../App';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  button: {
    maxWidth: '320px',
    maxHeight: '60px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  guestInfoTable: {
    flex: 1,
  },
  guestInfoSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  guestInfoDataKey: {
    width: '120px',
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
  particularsTable: {
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      alignSelf: 'center',
    },
  },
}));

interface CheckinNotesProps {}

const CheckinNotes = (props: CheckinNotesProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);
  const [loading, setLoading] = useState(false);

  const [checkedRooms, setCheckedRooms] = useState<any[]>(new Array<any>());
  const [selectedRoom, setSelectedRoom] = useState<any>();
  const [newNote, setNewNote] = useState<String>();
  const [additionalNotes, setAdditionalNotes] = useState<any[]>([]);

  useEffect(() => {
    getAllCheckedRooms();
  }, []);

  useEffect(() => {
    if (selectedRoom && selectedRoom.checkin_info_id) {
      getRoomNotes(selectedRoom.checkin_info_id);
    }
  }, [selectedRoom]);

  const getAllCheckedRooms = () => {
    setLoading(true);

    axios
      .get('/api/getAllCheckedRooms')
      .then((res) => res.data)
      .then((result) => {
        if (result.err) {
          alert(result.err);
        } else {
          if (result.checkedRooms && result.checkedRooms.length > 0) {
            setCheckedRooms(result.checkedRooms);
          } else {
            setCheckedRooms([]);
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const getRoomNotes = (checkinInfoId: number) => {
    setLoading(true);

    axios
      .get(`/api/getRoomNotes?checkinInfoId=${checkinInfoId}`)
      .then((res) => res.data)
      .then((result) => {
        if (result.err) {
          alert(result.err);
        } else {
          if (result.notes && result.notes.length > 0) {
            setAdditionalNotes(result.notes);
          } else {
            setAdditionalNotes([]);
          }
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const addRoomNote = () => {
    if (selectedRoom && newNote && newNote.trim().length > 0) {
      setLoading(true);
      axios
        .post('/api/addRoomNote', {
          checkinInfoId: selectedRoom.checkin_info_id,
          note: newNote.trim(),
        })
        .then((res) => res.data)
        .then((result) => {
          console.log(result);
          if (result.errno > 0) {
            alert(result.msg || 'Failed to add note!');
          } else {
            alert('Note Added!');
            setNewNote('');
            getRoomNotes(selectedRoom.checkin_info_id);
          }
        })
        .catch((err) => {
          console.log(err);
          alert('Failed to add note to the room');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      alert('No Room Selected!');
    }
  };

  const getNotesTable = () => {
    if (additionalNotes && additionalNotes.length > 0) {
      const StyledTableCell = withStyles((theme) => ({
        head: {
          backgroundColor: theme.palette.common.black,
          color: theme.palette.common.white,
        },
        body: {
          fontSize: 16,
        },
      }))(TableCell);

      const StyledTableRow = withStyles((theme) => ({
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }))(TableRow);

      return (
        <TableContainer component={Paper} className={classes.particularsTable}>
          <Table aria-label="customized table">
            <TableHead key="Headings">
              <TableRow>
                <StyledTableCell style={{ fontWeight: 800, fontSize: 18 }}>
                  Additional Notes
                </StyledTableCell>
                <StyledTableCell style={{ fontWeight: 800, fontSize: 18 }}>
                  Date
                </StyledTableCell>
                <StyledTableCell style={{ fontWeight: 800, fontSize: 18 }}>
                  Time
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {additionalNotes.map((row) => (
                <StyledTableRow key={row.note_id}>
                  <StyledTableCell>{row.note}</StyledTableCell>
                  <StyledTableCell width={100}>{row.note_date}</StyledTableCell>
                  <StyledTableCell width={100}>{row.note_time}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return '';
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Additional Notes</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <Autocomplete
              options={checkedRooms}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => `${r.room_name} - ${r.room_type}`}
              style={{ width: 300, marginTop: '24px', marginBottom: '24px' }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room #" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  setSelectedRoom(undefined);
                }
              }}
              value={selectedRoom || null}
            />
            <TextField
              label="Note"
              variant="outlined"
              placeholder="Add a note for this room"
              value={newNote || ''}
              onChange={(e) => setNewNote(e.target.value)}
              style={{ width: 300, marginTop: '32px' }}
            />
            <PrimaryButton onClick={addRoomNote} className={classes.button}>
              Add Note
            </PrimaryButton>
          </div>
          <hr className={classes.formSeparator} />
          <div className={classes.formContainer}>
            <label
              style={{
                margin: '8px',
                marginBottom: '32px',
                textDecoration: 'underline',
                color: '#004878',
                fontSize: '18px',
                fontWeight: 700,
                padding: '8px',
                verticalAlign: 'top',
              }}
            >
              Guest Info
            </label>
            <table className={classes.guestInfoTable}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey}>Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.first_name} {selectedRoom?.last_name}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Phone No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.contact}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>GST No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.gst_number}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Checkin Date:</td>
                  <td className={classes.guestInfoDataValue}>{`${
                    selectedRoom?.checkin_date
                      ? moment(selectedRoom?.checkin_date).format('DD/MM/YY') +
                        '  -  '
                      : ''
                  } ${selectedRoom?.checkin_time || ''}`}</td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Register No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.checkin_no}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Checkin Note:</td>
                  <td className={classes.guestInfoDataValue}>
                    {selectedRoom?.notes}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <hr className={classes.separatorLine} />
        {getNotesTable()}
      </div>
    );
  }
};

export default CheckinNotes;
